import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { resolveAvatarUrl } from '@/services/profile';
import type { DriverWithUser, DriverStatus } from '@/types/driver';
import {
  Briefcase,
  Calendar,
  Eye,
  MapPin,
  MoreHorizontal,
  Pencil,
  Phone,
  Car,
  FileCheck,
  AlertTriangle,
  XCircle,
  Clock,
} from 'lucide-react';

export type AdminDriverCardAction = 'view' | 'edit' | 'vehicles';

type CredentialStatus = {
  status: 'valid' | 'expiring' | 'expired' | 'missing' | 'grace_period' | 'pending';
  missing: number;
  total: number;
};

interface AdminDriverCardProps {
  driver: DriverWithUser & { credentialStatus?: CredentialStatus };
  onAction?: (action: AdminDriverCardAction, driver: DriverWithUser) => void;
}

/** Status config using native Badge variants per design system */
const statusStyles: Record<DriverStatus, { 
  label: string; 
  badgeVariant: 'default' | 'secondary' | 'destructive' | 'outline';
}> = {
  active: {
    label: 'Active',
    badgeVariant: 'default',
  },
  inactive: {
    label: 'Inactive',
    badgeVariant: 'secondary',
  },
  suspended: {
    label: 'Suspended',
    badgeVariant: 'destructive',
  },
  archived: {
    label: 'Archived',
    badgeVariant: 'outline',
  },
};

export function AdminDriverCard({ driver, onAction }: AdminDriverCardProps): JSX.Element {
  const navigate = useNavigate();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarLoading, setAvatarLoading] = useState(true);
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setAvatarLoading(true);
    setAvatarError(false);
    
    const load = async () => {
      const resolved = await resolveAvatarUrl(driver.user.avatar_url);
      if (isMounted) {
        setAvatarUrl(resolved);
        if (!resolved) {
          setAvatarLoading(false);
        }
      }
    };
    void load();
    return () => {
      isMounted = false;
    };
  }, [driver.user.avatar_url]);

  const statusStyle = statusStyles[driver.status];
  const initials = driver.user.full_name
    .split(' ')
    .map((n) => n.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');

  const handleClick = () => {
    navigate(`/admin/drivers/${driver.id}`);
  };

  // Format location
  const location = [driver.city, driver.state].filter(Boolean).join(', ');

  // Format last active
  const lastActive = driver.last_active_at
    ? new Date(driver.last_active_at).toLocaleDateString()
    : null;

  return (
    <Card className="h-full flex flex-col hover:shadow-soft transition-all">
      <CardContent className="p-4 space-y-3 flex-1 flex flex-col">
        {/* Header row with badge and actions */}
        <div className="flex items-center justify-between">
          <Badge variant={statusStyle.badgeVariant}>
            {statusStyle.label}
          </Badge>
          {/* Actions Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Driver actions">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onAction?.('view', driver)}>
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onAction?.('edit', driver)}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit Driver
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onAction?.('vehicles', driver)}>
                <Car className="h-4 w-4 mr-2" />
                Manage Vehicles
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Avatar and name centered */}
        <div className="flex flex-col items-center text-center cursor-pointer" onClick={handleClick}>
          {/* Driver Avatar */}
          <div 
            className="h-16 w-16 rounded-full border bg-muted/20 flex items-center justify-center overflow-hidden relative mb-2"
          >
            {/* Show skeleton while loading */}
            {avatarLoading && avatarUrl && (
              <Skeleton className="absolute inset-0 h-full w-full rounded-full" />
            )}
            {/* Render image (hidden while loading via opacity) */}
            {avatarUrl && !avatarError && (
              <img 
                src={avatarUrl} 
                alt={driver.user.full_name} 
                className={`h-full w-full object-cover transition-opacity ${avatarLoading ? 'opacity-0' : 'opacity-100'}`}
                onLoad={() => setAvatarLoading(false)}
                onError={() => {
                  setAvatarError(true);
                  setAvatarLoading(false);
                }}
              />
            )}
            {/* Show initials placeholder if no photo or error */}
            {(!avatarUrl || avatarError) && !avatarLoading && (
              <span className="text-primary font-semibold text-lg">{initials}</span>
            )}
          </div>

          {/* Driver Info */}
          <h3 className="text-base font-semibold">{driver.user.full_name}</h3>
          <p className="text-sm text-muted-foreground">{driver.user.email}</p>
        </div>

        {/* Metadata Section */}
        <div className="border-t pt-3 space-y-2 text-sm">
          {/* Credential Status */}
          {driver.credentialStatus && driver.credentialStatus.total > 0 && (
            <div className={`flex items-center gap-2 ${
              driver.credentialStatus.status === 'valid' ? 'text-primary' :
              driver.credentialStatus.status === 'expired' || driver.credentialStatus.status === 'missing' ? 'text-destructive' :
              driver.credentialStatus.status === 'expiring' || driver.credentialStatus.status === 'grace_period' ? 'text-amber-600 dark:text-amber-500' :
              'text-muted-foreground'
            }`}>
              {driver.credentialStatus.status === 'valid' ? (
                <>
                  <FileCheck className="h-4 w-4 flex-shrink-0" />
                  <span>Credentials complete</span>
                </>
              ) : driver.credentialStatus.status === 'expiring' ? (
                <>
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>{driver.credentialStatus.missing || 'Some'} credentials expiring</span>
                </>
              ) : driver.credentialStatus.status === 'expired' ? (
                <>
                  <XCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{driver.credentialStatus.missing} credentials expired</span>
                </>
              ) : driver.credentialStatus.status === 'missing' ? (
                <>
                  <XCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{driver.credentialStatus.missing} credentials missing</span>
                </>
              ) : driver.credentialStatus.status === 'grace_period' ? (
                <>
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>{driver.credentialStatus.missing} credentials due soon</span>
                </>
              ) : driver.credentialStatus.status === 'pending' ? (
                <>
                  <Clock className="h-4 w-4 flex-shrink-0" />
                  <span>Credentials pending review</span>
                </>
              ) : null}
            </div>
          )}

          {/* Employment Type */}
          <div className="flex items-center gap-2 text-muted-foreground">
            <Briefcase className="h-4 w-4 flex-shrink-0" />
            <span>{driver.employment_type === 'w2' ? 'W2 Employee' : '1099 Contractor'}</span>
          </div>

          {/* Phone */}
          <div className="flex items-center gap-2 text-muted-foreground">
            <Phone className="h-4 w-4 flex-shrink-0" />
            <span>{driver.user.phone || 'No phone'}</span>
          </div>

          {/* Location Assignment */}
          {driver.location && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4 flex-shrink-0" />
              <span>{driver.location.name}</span>
            </div>
          )}

          {/* Location */}
          {location && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4 flex-shrink-0" />
              <span>{location}</span>
            </div>
          )}

          {/* Last Active */}
          {lastActive && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4 flex-shrink-0" />
              <span>Last active: {lastActive}</span>
            </div>
          )}
        </div>

        {/* Spacer to push button to bottom */}
        <div className="flex-1" />

        {/* View Button */}
        <Button variant="outline" size="sm" className="w-full mt-auto" onClick={handleClick}>
          <Eye className="h-4 w-4 mr-2" />
          View Details
        </Button>
      </CardContent>
    </Card>
  );
}
